import React, { useEffect, useMemo, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useLocation, useOutletContext } from "react-router-dom";
import { gql, useMutation, useQuery } from "@apollo/client";
import Skeleton from "react-loading-skeleton";
import { IconChevronsRight, IconLoader3, IconSquareRoundedXFilled } from "@tabler/icons-react";
import { AnyPrice, castPrice, toGQLDate, useAppConfig } from "@ct-react/core";
import { useLocaleNavigate, useLocaleUrl } from "@ct-react/locale";
import { BookFullPrice, CartPaymentAmount, CartPaymentMode } from "@ct-react/calendar";
import { FULL_CART_GQL_DATA, SHORT_ARTICLE_GQL_METADATA } from "@shared/gql/queries";
import { CartItem as CartItemModel } from "../../models/cart";
import { cartTranslations } from "../../i18n/sharable-defs";
import { useApolloMore } from "../../hooks/generic";
import { useCartBadge, useCheckCartExpiration } from "../../hooks/cart-step-check";
import { CartContext } from "./cart-outlet";
import { Button } from "../../components/common/minimals";
import Modal, { InnerModal } from "../../components/common/modal";
import PaymentTypeChoiceForm from "../../components/forms/payment-type-choice";
import { CartPaymentResult } from "../../components/cart/cart-price";
import "./common.scss";
import "./payment.scss";

const transDefs = defineMessages({
  expiredTitle: { id: "cart-process-expired-title", defaultMessage: "Votre panier est vide." },
  expiredMsg: { id: "cart-process-expired-msg", defaultMessage: "Le blocage de l'hébergement vous permettant de procéder à la réservation a expiré. Veuillez recommencer si vous souhaitez encore réserver." }
});

const CART_CHECKOUT_GQL_MUT = gql`
  mutation CartCheckout($sessionId: String, $userId: String, $checkout: CheckoutCartInput!) {
    cartCheckout(sessionId: $sessionId, userId: $userId, checkout: $checkout) @recaptcha(minScore: 0.8, action: "checkout") { paymentUrl }
  }
`;

type ExpiredCartProps = {
  open: boolean;
  onConfirmation: () => void;
}

export const ExpiredCart = (
  {
    open: openProps,
    onConfirmation
  }: ExpiredCartProps) => {

  const intl = useIntl();
  const [ open, setOpen ] = useState<boolean>(false);

  useEffect(() => {
    openProps && setOpen(true)
  }, [ openProps ]);

  const onClosing = (state: boolean) => {
    setOpen(state);
    !state && onConfirmation();
  }

  const buttonRendering = useMemo(() => (
    <Button type="button"
            className="link bolder small inline-icon"
            onClick={() => onClosing(false)}>
      {intl.formatMessage(cartTranslations.backHome)}
      <IconChevronsRight />
    </Button>
  ), []);

  return (
    <Modal size="sm"
           closableCross={false}
           open={open}
           onOpenChange={onClosing}>
      <InnerModal.Content className="expired-cart-modal"
                          footer={buttonRendering}>
        <div className="expired-message">
          <IconSquareRoundedXFilled/>
          <span>{intl.formatMessage(transDefs.expiredTitle)}</span>
        </div>
        <p>{intl.formatMessage(transDefs.expiredMsg)}</p>
      </InnerModal.Content>
    </Modal>);
}

const CartPayment = () => {

  const intl = useIntl();
  const { baseUrl } = useAppConfig();
  const navigate = useLocaleNavigate();
  const { state } = useLocation();
  const { buildLocaleHref } = useLocaleUrl();
  const { mutation } = useApolloMore();

  const itemsCount = useCartBadge();
  const { sessionId, userId, changeIndex } = useOutletContext<CartContext>();
  const [ paymentMode, setPaymentMode ] = useState<CartPaymentMode>("full");
  const [ paymentDetail, setPaymentDetail ] = useState<CartPaymentAmount>();
  const [ checking, setChecking ] = useState<boolean>(false);
  const [ expiredCart, setExpiredCart ] = useState<boolean>(false);

  useEffect(() => {
    itemsCount === 0 && navigate("/cart");
    !state?.chosenVisitorType && navigate("/cart/account", { state });
    !state?.visitorInfo && navigate("/cart/information", { state });
    changeIndex(3);
  }, []);

  // data

  const { data, loading } = useQuery(FULL_CART_GQL_DATA, { variables: { sessionId, userId } });
  const checkCartExpiration = useCheckCartExpiration();

  const firstCartItem = data?.cart?.items?.length ? (data.cart.items[0] as CartItemModel) : undefined;
  const articleId = firstCartItem?.articleId;
  const period = firstCartItem?.period;

  useQuery(SHORT_ARTICLE_GQL_METADATA, {
    variables: { articleId },
    skip: !articleId,
    ssr: false,
    onCompleted: ({ detail, agency }) => setContent({ summary: detail, agency })
  });

  const [ content, setContent ] = useState<any | null>(null);

  const [ cartCheckout ] = useMutation(CART_CHECKOUT_GQL_MUT);

  // dom interactions

  const onPaymentChoice = (mode: CartPaymentMode, detail: CartPaymentAmount) => {
    setPaymentMode(mode);
    setPaymentDetail(detail);
  }

  const onPay = async () => {
    setChecking(true);
    checkCartExpiration()
      .catch(() => {
        setExpiredCart(true);
        setChecking(false);
      })
      .then(() => {
        const variables = {
          sessionId,
          userId,
          checkout: {
            customer: state?.visitorInfo || {},
            hasFullPayment: paymentMode === "full",
            redirect: {
              baseUrl,
              successPath: buildLocaleHref("/cart/payment/success"),
              cancelPath: buildLocaleHref("/cart/payment/cancellation")
            }
          }
        };
        return mutation.withRecaptcha(cartCheckout, "checkout")({ variables });
      })
      .then(({ data: { cartCheckout } }) => !cartCheckout.paymentUrl
        ? navigate("/cart/payment/success")
        : window.location.href = cartCheckout.paymentUrl);
  }

  // rendering

  if (loading || !data || !data?.cart)
    return (<Skeleton className="cart-skeleton" />);

  const isReady = !!data?.cart && !!period && !!content?.summary;
  let checkin: any, checkout: any;
  if (isReady) {
    [checkin, checkout] = period as any[]; // period is ['YYYY-MM-DD','YYYY-MM-DD']
  }

  return (
    <>

      <ExpiredCart open={expiredCart}
                   onConfirmation={() => navigate("/")} />

      <div className="cart-item-wrapper">

        <div className="rl-two-col">
          <div>
            <h2>{intl.formatMessage(cartTranslations.paymentModel)}</h2>
            <PaymentTypeChoiceForm items={data.cart.items}
                                   onChoice={onPaymentChoice} />
          </div>
          <CartPaymentResult cartPrice={data.cart.fullPrice}
                             mode={paymentMode}
                             detail={paymentDetail} />
        </div>

        <div className="rl-two-col stepper-actions">

          <button type="button"
                  className="back step-btn"
                  onClick={() => navigate("/cart/information", {state})}
                  disabled={checking}>
            {intl.formatMessage(cartTranslations.backInfo)}
          </button>

          {isReady &&
            <button type="button"
                    className="step-btn"
                    disabled={checking || !paymentDetail}
                    onClick={onPay}
                    data-tm-id="to-purchase"
                    data-domain={baseUrl}
                    data-name={content!.summary.technicalName}
                    data-agency={content!.agency.name}
                    data-provider={content!.agency.id}
                    data-price={castPrice((data!.cart.fullPrice as BookFullPrice).amount as AnyPrice).amount}
                    data-checkin={toGQLDate(checkin)}
                    data-checkout={toGQLDate(checkout)}>
              {checking
                ? <IconLoader3 className="loading-action-tabler" />
                : intl.formatMessage(cartTranslations.nextPay)
              }
            </button>
          }

        </div>

      </div>
    </>);

}

export default CartPayment;
